import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Settings, Layout, Layers, Database, CreditCard, Home, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { UserProfileAuth } from "@/components/UserProfileAuth";
import { useSidebar } from "@/context/SidebarContext";
import { cn } from "@/lib/utils";
import { OrganizationListbox } from "@/components/OrganizationListbox";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useOrganization } from "@/context/OrganizationContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const menuItems = [
  {
    label: "Leia-me",
    icon: BookOpen,
    id: "Leiame",
    path: "/leia-me"
  },
  {
    label: "Dashboard",
    icon: Home,
    id: "Dashboard",
    path: "/"
  },
  {
    label: "Diagramas",
    icon: Layout,
    id: "Diagramas",
    path: "/diagramas"
  },
  {
    label: "Componentes",
    icon: Layers,
    id: "Componentes",
    path: "/componentes"
  },
  {
    label: "Tipos de Componentes",
    icon: Database,
    id: "TiposComponentes",
    path: "/tipos-componentes"
  },
  {
    label: "Configurações",
    icon: Settings,
    id: "Configuracoes",
    path: "/configuracoes"
  }
];

export function Sidebar() {
  const { isCollapsed, toggleSidebar, currentPage, setCurrentPage } = useSidebar();
  const { currentOrganization } = useOrganization();
  const { user, subscriptionData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: diagramId } = useParams();
  const [diagramName, setDiagramName] = useState<string>("");
  const [showPlanChange, setShowPlanChange] = useState(false);

  useEffect(() => {
    const checkPlanChangeVisibility = async () => {
      if (!user || !subscriptionData.subscription_id) {
        setShowPlanChange(false);
        return;
      }

      try {
        const { data: subscriberData, error } = await supabase
          .from('subscribers')
          .select('user_id, subscription_tier, subscribed')
          .eq('id', subscriptionData.subscription_id)
          .single();

        if (error) {
          setShowPlanChange(false);
          return;
        }

        const isOwner = subscriberData?.user_id === user.id;
        const isFree = !subscriberData?.subscribed || 
                      !subscriberData?.subscription_tier || 
                      subscriberData.subscription_tier.toLowerCase() === 'free';

        setShowPlanChange(isOwner && isFree);

      } catch (error) {
        setShowPlanChange(false);
      }
    };

    checkPlanChangeVisibility();
  }, [user, subscriptionData.subscription_id]);

  const dynamicMenuItems = [
    ...menuItems,
    ...(showPlanChange ? [{
      label: "Mude aqui seu plano",
      icon: CreditCard,
      id: "MudePlano",
      path: "/planos"
    }] : [])
  ];

  const handleMenuClick = (id: string, path: string) => {
    setCurrentPage(id);
    navigate(path);
  };

  useEffect(() => {
    const fetchDiagramName = async () => {
      if (!diagramId) {
        setDiagramName("");
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('diagrams')
          .select('name')
          .eq('id', diagramId)
          .single();
          
        if (error) {
          setDiagramName("");
          return;
        }
        
        if (data) {
          setDiagramName(data.name);
        }
      } catch (error) {
        setDiagramName("");
      }
    };
    
    fetchDiagramName();
  }, [diagramId]);
  
  return (
    <div 
      className={cn(
        "bg-white border-r border-gray-200 h-screen flex flex-col justify-between transition-all duration-300",
        isCollapsed ? "w-[70px]" : "w-[250px]"
      )}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {!isCollapsed && (
          <div className="flex items-center justify-center w-full">
            <img 
              src="/lovable-uploads/f4aaa22d-c802-4c7c-b9cb-436f6835f2f2.png" 
              alt="Mozarch" 
              className="h-8 w-auto"
            />
          </div>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleSidebar} 
          className="rounded-full hover:bg-gray-100"
        >
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          )}
        </Button>
      </div>
      
      <div className="mt-6">
        <UserProfileAuth />
      </div>

      {!isCollapsed && (
        <div className="px-2 mt-2">
          <OrganizationListbox />
        </div>
      )}
      
      <nav className="flex-1 px-2 py-6">
        <ul className="space-y-2">
          {dynamicMenuItems.map((item) => (
            <li key={item.id}>
              <Button
                variant="ghost"
                onClick={() => handleMenuClick(item.id, item.path)}
                className={cn(
                  "w-full flex items-center gap-3 justify-start rounded-lg py-2 text-gray-600 hover:bg-gray-100",
                  currentPage === item.id && "bg-primary/10 text-primary hover:bg-primary/10"
                )}
              >
                <item.icon className={cn(
                  "h-5 w-5",
                  currentPage === item.id ? "text-primary" : "text-gray-500"
                )} />
                {!isCollapsed && <span>{item.label}</span>}
              </Button>
            </li>
          ))}
        </ul>
      </nav>
      
      <div className="p-4 border-t border-gray-200">
      </div>
    </div>
  );
}
